<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import Header from './component/header.vue'

const route  = useRoute()
const router = useRouter()

const loading  = ref(false)
const saving   = ref(false)
const review   = ref(null)
const goals    = ref([])
const kpis     = ref([])
const comps    = ref([])
const ratings  = ref({})
const summary  = ref('')
const formError = ref('')

const reviewId = computed(() => route.params.id)

const load = async () => {
  try {
    loading.value = true
    const res = await axios.get(`/hr/pms/reviews/${reviewId.value}`)
    review.value = res.data.data

    const cycleId = review.value.cycle_id
    const empId   = review.value.employee_id

    const [g, k, c] = await Promise.all([
      axios.get('/hr/pms/goals', { params: { cycle_id: cycleId, employee_id: empId, per_page: 100 } }),
      axios.get('/hr/pms/kpis',  { params: { per_page: 100 } }),
      axios.get('/hr/pms/competencies', { params: { per_page: 100 } }),
    ])
    goals.value = g.data.data?.data ?? []
    kpis.value  = k.data.data?.data ?? []
    comps.value = c.data.data?.data ?? []

    const existing = review.value.ratings ?? []
    existing.forEach(r => {
      ratings.value[`${r.ratable_type}::${r.ratable_id}`] = {
        rating: r.rating, comment: r.comment ?? '',
      }
    })
    summary.value = review.value.summary_comments ?? ''
  } catch (e) { console.error(e) } finally { loading.value = false }
}

onMounted(load)

const ratingKey = (type, id) => `${type}::${id}`

const getRating = (type, id) => ratings.value[ratingKey(type, id)] ?? { rating: 0, comment: '' }

const setRating = (type, id, val) => {
  const k = ratingKey(type, id)
  if (!ratings.value[k]) ratings.value[k] = { rating: 0, comment: '' }
  ratings.value[k].rating = Number(val)
}

const setComment = (type, id, val) => {
  const k = ratingKey(type, id)
  if (!ratings.value[k]) ratings.value[k] = { rating: 0, comment: '' }
  ratings.value[k].comment = val
}

const saveRatings = async () => {
  try {
    saving.value = true
    formError.value = ''
    const payload = Object.entries(ratings.value).map(([k, v]) => {
      const [type, id] = k.split('::')
      return { ratable_type: type, ratable_id: id, rating: v.rating, comment: v.comment }
    })
    await axios.post(`/hr/pms/reviews/${reviewId.value}/ratings`, { ratings: payload, summary_comments: summary.value })
    formError.value = ''
    alert('Ratings saved successfully')
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to save ratings'
  } finally { saving.value = false }
}

const submit = async () => {
  if (!confirm('Submit this review? It cannot be edited after submission.')) return
  try {
    saving.value = true
    await axios.put(`/hr/pms/reviews/${reviewId.value}/submit`)
    router.push({ name: 'PMS Reviews' })
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to submit'
  } finally { saving.value = false }
}
</script>

<template>
  <div>
    <Header />
    <div class="pms-page">
      <div v-if="loading" class="tbl-center">Loading review…</div>
      <template v-else-if="review">
        <div class="rf-header">
          <h2>Review Form <span class="rf-type-badge">{{ review.review_type }}</span></h2>
          <span :class="['badge', review.status === 'submitted' ? 'badge--green' : 'badge--orange']">{{ review.status }}</span>
        </div>
        <div v-if="formError" class="form-error">{{ formError }}</div>

        <!-- Goals section -->
        <div v-if="goals.length" class="rf-section">
          <h3 class="rf-section-title">Goals</h3>
          <div v-for="g in goals" :key="g.id" class="rf-item">
            <div class="rf-item-label">{{ g.title }} <span class="rf-weight">({{ g.weight }}%)</span></div>
            <div class="rf-item-fields">
              <div class="form-group">
                <label>Rating (0–5)</label>
                <input type="number" :value="getRating('goal', g.id).rating" @input="setRating('goal', g.id, $event.target.value)" min="0" max="5" step="0.5" />
              </div>
              <div class="form-group form-group--wide">
                <label>Comment</label>
                <input :value="getRating('goal', g.id).comment" @input="setComment('goal', g.id, $event.target.value)" placeholder="Optional comment" />
              </div>
            </div>
          </div>
        </div>

        <!-- KPIs section -->
        <div v-if="kpis.length" class="rf-section">
          <h3 class="rf-section-title">KPIs</h3>
          <div v-for="k in kpis" :key="k.id" class="rf-item">
            <div class="rf-item-label">{{ k.title }} <span class="rf-weight">({{ k.weight }}%)</span></div>
            <div class="rf-item-fields">
              <div class="form-group">
                <label>Rating (0–5)</label>
                <input type="number" :value="getRating('kpi', k.id).rating" @input="setRating('kpi', k.id, $event.target.value)" min="0" max="5" step="0.5" />
              </div>
              <div class="form-group form-group--wide">
                <label>Comment</label>
                <input :value="getRating('kpi', k.id).comment" @input="setComment('kpi', k.id, $event.target.value)" placeholder="Optional comment" />
              </div>
            </div>
          </div>
        </div>

        <!-- Competencies section -->
        <div v-if="comps.length" class="rf-section">
          <h3 class="rf-section-title">Competencies</h3>
          <div v-for="c in comps" :key="c.id" class="rf-item">
            <div class="rf-item-label">{{ c.name }} <span class="rf-weight">({{ c.weight }}%)</span></div>
            <div class="rf-item-fields">
              <div class="form-group">
                <label>Rating (0–5)</label>
                <input type="number" :value="getRating('competency', c.id).rating" @input="setRating('competency', c.id, $event.target.value)" min="0" max="5" step="0.5" />
              </div>
              <div class="form-group form-group--wide">
                <label>Comment</label>
                <input :value="getRating('competency', c.id).comment" @input="setComment('competency', c.id, $event.target.value)" placeholder="Optional comment" />
              </div>
            </div>
          </div>
        </div>

        <!-- Summary -->
        <div class="rf-section">
          <h3 class="rf-section-title">Overall Summary</h3>
          <textarea v-model="summary" rows="4" class="full-textarea" placeholder="Summarize the performance review…"></textarea>
        </div>

        <div class="rf-actions">
          <button class="btn-outline" @click="saveRatings" :disabled="saving || review.status !== 'draft'">{{ saving ? 'Saving…' : 'Save Draft' }}</button>
          <button class="btn-primary" @click="submit" :disabled="saving || review.status !== 'draft'">Submit Review</button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.pms-page     { padding: 0 24px 40px; max-width: 860px; }
.rf-header    { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
.rf-header h2 { font-size: 20px; font-weight: 700; color: #1a2a45; margin: 0; }
.rf-type-badge { font-size: 12px; font-weight: 600; background: #eef4ff; color: #004AAD; padding: 3px 10px; border-radius: 99px; text-transform: capitalize; }
.rf-section   { background: #fff; border: 1px solid #eaf0fb; border-radius: 10px; padding: 20px 24px; margin-bottom: 20px; }
.rf-section-title { font-size: 15px; font-weight: 700; color: #1a2a45; margin: 0 0 16px; }
.rf-item      { border-top: 1px solid #f0f3fa; padding: 14px 0; }
.rf-item:first-of-type { border-top: none; padding-top: 0; }
.rf-item-label { font-size: 14px; font-weight: 600; color: #1a2a45; margin-bottom: 10px; }
.rf-weight    { font-size: 12px; color: #888; font-weight: 400; }
.rf-item-fields { display: flex; gap: 14px; }
.form-group   { display: flex; flex-direction: column; gap: 5px; }
.form-group--wide { flex: 1; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input { padding: 8px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; width: 90px; }
.form-group--wide input { width: 100%; box-sizing: border-box; }
.full-textarea { width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; resize: vertical; }
.rf-actions   { display: flex; gap: 12px; justify-content: flex-end; margin-top: 8px; }
.tbl-center   { text-align: center; color: #999; padding: 60px; }
.badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; }
.badge--green  { background: #d1e7dd; color: #0f5132; }
.badge--orange { background: #fff3cd; color: #856404; }
.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.form-error   { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 16px; }
</style>
